import { JSX, useContext, useEffect, useCallback } from "react";
import { AccuracyReportsContext } from "@app/reports/accuracy-reports/import/AccuracyReportsContext";
import { WizardStepProps } from "@app/reports/accuracy-reports/import/interfaces";
import { ReportSettingsBody, ReportSettingsSaveAction } from "@app/exports/export-panel-body/report-settings";
import AccuracySelectEntityTypeFilter from "@app/exports/export-panel-body/filters/AccuracySelectEntityTypeFilter";
import AccuracyRowTypeFilter from "@app/exports/export-panel-body/filters/AccuracyRowTypeFilter";
import AccuracyRecommendedActionFilter from "@app/exports/export-panel-body/filters/AccuracyRecommendedActionFilter";
import { ReportSettingsType } from "@app/exports/enumerations";
import { ReportSettingsModel } from "@app/exports/exportModels";
import MultiselectDropdown from "@app/shared/checkbox-dropdown/MultiselectDropdown";
import { DropdownItem } from "@app/shared/checkbox-dropdown/types";

const AccuracyReportsSelectActionableProviders = ({ setPageIsValid }: WizardStepProps): JSX.Element => {
  const context = useContext(AccuracyReportsContext);

  useEffect(() => {
    if (setPageIsValid) {
      const isValid = 
        (context.entityTypeFilter?.includePractitionerEntityType || context.entityTypeFilter?.includeServiceLocationEntityType) &&
        (context.rowTypeIds?.length > 0) &&
        (context.recommendedActionIds?.length > 0 || context.showAllRecommendedActionReasons);
      setPageIsValid(isValid);
    }
  }, [
    setPageIsValid, 
    context.entityTypeFilter, 
    context.rowTypeIds, 
    context.recommendedActionIds, 
    context.showAllRecommendedActionReasons
  ]);


  const buildReportSettingsModel = useCallback((isDefault: boolean, name: string): ReportSettingsModel => {
    return context.buildReportSettingsModel?.(isDefault, name) ?? {
      isDefault,
      name,
      reportSettingsJson: "{}",
      type: ReportSettingsType.AccuracyActionableProviders,
    } as ReportSettingsModel;
  }, [context.buildReportSettingsModel]);

  return (
    <div className="accuracy-reports-select-actionable-providers">
      <ReportSettingsBody
        buildReportSettingsModel={buildReportSettingsModel}
        createReportSettingsNameInputDisabled={context.createReportSettingsNameInputDisabled ?? true}
        currentReportSettings={context.currentReportSettings}
        isSelectionValid={context.isSelectionValid ?? true}
        parseReportSettingsJsonToState={context.parseReportSettingsJsonToState ?? (() => {})}
        reportSettingsHasChanges={context.reportSettingsHasChanges ?? false}
        reportSettingsHasInvalidChanges={context.reportSettingsHasInvalidChanges ?? false}
        reportSettingsList={context.reportSettingsList ?? []}
        saveClicked={context.saveClicked ?? false}
        setCreateReportSettingsNameInputDisabled={context.setCreateReportSettingsNameInputDisabled ?? (() => {})}
        setCurrentReportSettings={context.setCurrentReportSettings ?? (() => {})}
        setReportSettingsIsSelectionValid={context.setIsSelectionValid ?? (() => {})}
        setReportSettingsHasChanges={context.setReportSettingsHasChanges ?? (() => {})}
        setReportSettingsHasInvalidChanges={context.setReportSettingsHasInvalidChanges ?? (() => {})}
        setReportSettingsList={context.setReportSettingsList ?? (() => {})}
        setSaveClicked={context.setSaveClicked ?? (() => {})}
      />

      <div className="row mt-4">
        <div className="col-xl-5 col-lg-6">
          <div style={{ width: '100%' }} className="accuracy-recommended-action-override">
            <AccuracyRecommendedActionFilter
              onRecommendedActionChange={(id) => {
                const currentIds = [...(context.recommendedActionIds ?? [])];
                const index = currentIds.indexOf(id);
                if (index > -1) {
                  currentIds.splice(index, 1);
                } else {
                  currentIds.push(id);
                }
                context.setRecommendedActionIds?.(currentIds);
              }}
              onRecommendedActionChangeDropdown={context.handleRecommendedActionChangeDropdown ?? (() => {})}
              recommendedActionIds={context.recommendedActionIds ?? []}
              recommendedActionReasonsFilterDisabled={false}
              onShowAllReasonsChange={context.handleShowAllReasonsChange ?? (() => {})}
              showAllRecommendedActionReasons={context.showAllRecommendedActionReasons ?? true}
            />
            <style>{`
              .accuracy-recommended-action-override .col-xl-10,
              .accuracy-recommended-action-override .col-lg-9 {
                flex: 0 0 100% !important;
                max-width: 100% !important;
              }
              .accuracy-recommended-action-override .form-check {
                padding-left: 1.5rem !important;
              }
            `}</style>
          </div>
        </div>
      </div>

      <div className="row mt-4">
        <div className="col-xl-5 col-lg-6">
          <AccuracySelectEntityTypeFilter
            disabled={false}
            onEntityTypeIncludeChange={(selected, isPractitioner) => {
              const newFilter = {
                includePractitionerEntityType: isPractitioner ? selected : (context.entityTypeFilter?.includePractitionerEntityType ?? true),
                includeServiceLocationEntityType: isPractitioner ? (context.entityTypeFilter?.includeServiceLocationEntityType ?? true) : selected,
              };
              context.setEntityTypeFilter?.(newFilter);
            }}
            onEntityTypeIncludeChangeDropdown={context.handleEntityTypeIncludeChangeDropdown ?? (() => {})}
            entityTypeFilter={context.entityTypeFilter ?? { includePractitionerEntityType: true, includeServiceLocationEntityType: true }}
          />
        </div>
      </div>

      <div className="row mt-3">
        <div className="col-xl-5 col-lg-6">
          <AccuracyRowTypeFilter
            onRowTypeChange={(id) => {
              const currentIds = [...(context.rowTypeIds ?? [])];
              const index = currentIds.indexOf(id);
              if (index > -1) {
                currentIds.splice(index, 1);
              } else {
                currentIds.push(id);
              }
              context.setRowTypeIds?.(currentIds);
            }}
            onRowTypeChangeDropdown={context.handleRowTypeChangeDropdown ?? (() => {})}
            rowTypeIds={context.rowTypeIds ?? []}
          />
        </div>
      </div>

      <div className="row mt-3">
        <div className="col-xl-5 col-lg-6">
          <p className="lead mb0 demibold">Recommended Action Reasons Filter</p>
          <p className="mb-1">Please select the reasons to include:</p>
          <MultiselectDropdown
            containerClassName="w-100"
            customSelectAllText="All Available Reasons"
            customSelectInputText={`${(context.selectedRecommendedActionReasons ?? []).length} of ${(context.recommendedActionReasons ?? []).length} options selected`}
            deselectAllOnSearch={true}
            items={(context.recommendedActionReasons ?? []).map(model => ({ key: model.reason, name: model.friendlyName }))}
            label="Recommended Action Reasons"
            hideLabel={true}
            onSelectionChanged={context.handleRecommendedActionReasonDropdownChange ?? (() => {})}
            selectAllAppliesToAllItems={true}
            selectedItems={(context.selectedRecommendedActionReasons ?? []).map((x): DropdownItem<string> => ({ key: x, name: null }))}
            testId="recommendedActionReasons-multiselect-test-id"
            disabled={false}
          />
        </div>
      </div>

      <ReportSettingsSaveAction
        currentReportSettings={context.currentReportSettings}
        isSelectionValid={context.isSelectionValid ?? true}
        reportSettingsHasChanges={context.reportSettingsHasChanges ?? false}
        reportSettingsHasInvalidChanges={context.reportSettingsHasInvalidChanges ?? false}
        reportSettingsList={context.reportSettingsList ?? []}
        saveClicked={context.saveClicked ?? false}
        setCreateReportSettingsNameInputDisabled={context.setCreateReportSettingsNameInputDisabled ?? (() => {})}
        setCurrentReportSettings={context.setCurrentReportSettings ?? (() => {})}
        setReportSettingsHasChanges={context.setReportSettingsHasChanges ?? (() => {})}
        setReportSettingsList={context.setReportSettingsList ?? (() => {})}
        setSaveClicked={context.setSaveClicked ?? (() => {})}
        stringifyStateReportSettings={context.stringifyStateReportSettings ?? (() => "{}")}
      />
    </div>
  );
};

export default AccuracyReportsSelectActionableProviders;